import React from "react";
import { NumericFormat } from "react-number-format";
import moment from "moment";
import { INPUT_TYPES } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { useTheme } from "@mui/system";
import { MiAutocomplete } from "../MiAutocomplete/MiAutocomplete";
import { MiCheckbox } from "../MiCheckbox/MiCheckbox";
import { MiRadioGroup } from "../MiControlledRadioGroup/MiRadioGroup";
import { MiInputMultiSelectBox } from "../MiInputMultiSelectBox/MiInputMultiSelectBox";
import { MiInputSelectBox } from "../MiInputSelectBox/MiInputSelectBox";
import { MiInputTextbox } from "../MiInputTextBox/MiInputTextBox";
import { MiSwitch } from "../MiSwitch/MiSwitch";
import { MiTimePicker } from "../MiTimePicker/MiTimePicker";

export const MiInput = ({ item }) => {
  const theme = useTheme();

  switch (item.fieldType) {
    case INPUT_TYPES.SELECT:
      return (
        <MiInputSelectBox
          name={item.name}
          fieldType={item.fieldType}
          label={item.label}
          value={item.value}
          minwidth={item.minWidth}
          options={item.options || []}
          disabled={item.disabled}
          readOnly={item.readOnly}
          fullWidth={item.fullWidth}
          required={item.required}
          size={item.size}
          disableNone={!item.selectNone}
          style={item.style}
          {...item}
        />
      );
    case INPUT_TYPES.AUTOCOMPLETE:
      return (
        <MiAutocomplete
          label={item.label}
          inputValue={item.inputValue}
          onInputChange={item.onInputChange}
          onChange={item.handleChange}
          value={item.value}
          variant={item.variant || "standard"}
          options={item.options}
          required={item.required}
          readOnly={item.readOnly}
          disabled={item.disabled}
          freeSolo={item.freeSolo || false}
          filterOptions={item.filterOptions}
          renderOption={item.renderOption}
          minwidth={item.minWidth}
          forcePopupIcon={item.forcePopupIcon}
          handleClear={item.handleClear}
          disablePortal={item.disablePortal ?? false}
          containerStyles={item.containerStyles}
          {...item}
        />
      );
    case INPUT_TYPES.CHECKBOX:
      return (
        <MiCheckbox
          label={item.label}
          disabled={item.readOnly}
          icon={item.icon}
          checkedIcon={item.checkedIcon}
          labelPlacement={item.labelPlacement}
          {...item}
        />
      );
    case INPUT_TYPES.SWITCH:
      return <MiSwitch label={item.label} disabled={item.readOnly} {...item} />;
    case INPUT_TYPES.EMPTY_FIELD:
      return <div style={{ diplay: "none" }} />;
    case INPUT_TYPES.CUSTOM:
      return React.cloneElement(item.customComponent, { ...item });
    case INPUT_TYPES.RADIO_GROUP:
      return (
        <MiRadioGroup
          name={item.name}
          label={item.label}
          helperText={item.helperText}
          options={item.options}
          disabled={item.readOnly}
          row={item.row}
          radioSize={item.radioSize}
          sx={item.sx}
          value={item.value}
          handleChange={item.handleChange}
          {...item}
        />
      );
    case INPUT_TYPES.MULTI_SELECT:
      return (
        <MiInputMultiSelectBox
          value={item.value}
          labelText={item.label}
          menuItems={item.options}
          disableNone={!item.selectNone}
          readOnly={item.readOnly}
          disabled={item.disabled}
          required={item.required}
          multiline={item.multiline}
          error={!!item.error}
          minWidth={item.minWidth}
          marginBottom={item.marginBottom}
          marginRight={item.marginRight}
          helperText={item.helperText}
          placeholder={item.placeholder}
          rows={item.rows}
          type={item.fieldType}
          clickButton={item.clickButton}
          InputLabelProps={{ style: { color: theme.palette.grey.darkAccent } }}
          InputProps={item.InputProps}
          {...item}
        />
      );
    case INPUT_TYPES.CURRENCY:
      return (
        <NumericFormat
          customInput={MiInputTextbox}
          thousandSeparator
          label={item.label}
          decimalScale={2}
          fixedDecimalScale
          allowNegative={item.allowNegative || false}
          allowLeadingZeros={false}
          prefix="$"
          name={item.name}
          disabled={item.readOnly}
          onValueChange={(values, sourceInfo) => {
            item.handleChange(sourceInfo, values);
          }}
          {...item.inputTextboxProps}
        />
      );
    case INPUT_TYPES.NUMERIC:
      return (
        <NumericFormat
          customInput={MiInputTextbox}
          name={item.name}
          label={item.label}
          value={item.value}
          onValueChange={(values, sourceInfo) => {
            item.handleChange(sourceInfo, values);
          }}
          thousandSeparator={item.thousandSeparator}
          decimalScale={item.decimalScale}
          fixedDecimalScale={item.fixedDecimalScale}
          allowNegative={item.allowNegative}
          allowLeadingZeros={item.allowLeadingZeros}
          disabled={item.disabled}
          suffix={item.suffix}
          prefix={item.prefix}
          {...item.inputTextboxProps}
        />
      );
    case INPUT_TYPES.TIME_PICKER: {
      const timeFormat = item.format || "HH:mm";
      return (
        <MiTimePicker
          {...item}
          format={timeFormat}
          label={item.label}
          onChange={(value) =>
            value ? item.handleChange(formatDate(value, timeFormat)) : ""
          }
          value={item.value ? moment(item.value, timeFormat) : null}
        />
      );
    }
    case INPUT_TYPES.TEXTBOX:
    default:
      return (
        <MiInputTextbox
          name={item.name}
          disabled={item.disabled}
          readOnly={item.readOnly}
          valueModifier={item.valueModifier}
          type={item.fieldType}
          {...item}
        />
      );
  }
};
